/**
 * Plasmic component registration file.
 *
 * IMPORTANT: This file MUST NOT have "use client" directive.
 *
 * Why? The @plasmicapp/loader-nextjs/react-server-conditional module creates
 * SEPARATE PLASMIC instances for server and client module graphs. The
 * PLASMIC.unstable__getServerQueriesData() in page.tsx uses the SERVER instance.
 * If registrations happen in a "use client" module, they go to the CLIENT
 * instance, leaving the SERVER instance empty — causing "Unsupported Server
 * Component type: undefined" errors.
 *
 * To avoid "use client", we use React.lazy() for components that have deep
 * client-only import chains (useState, createContext, etc.). This prevents
 * the actual component modules from being eagerly imported at the module level.
 *
 * Components that already have "use client" at their own entry point are
 * imported directly — they become client references on the server, which is fine.
 */
import { PLASMIC } from "@stadium/services/plasmic";

import { CatalogEntriesPlasmicProps } from "@components/swagmagic/dynamic/slices/catalog-entries/plasmic-props";
import { ProductListingCarouselPlasmicProps } from "@components/swagmagic/dynamic/slices/product-listing-carousel/plasmic-props";
import { ShopMenuWrapperPlasmicProps } from "@components/swagmagic/dynamic/slices/shop-menu-wrapper/plasmic-props";
import { BANNER_PLASMIC_PROPS } from "@stadium/shared/src/components/plasmic/Banner/plasmic-props";
import { HERO_PLASMIC_PROPS } from "@stadium/shared/src/components/plasmic/Hero/plasmic-props";
import { LOGO_STRIPS_PLASMIC_PROPS } from "@stadium/shared/src/components/plasmic/LogoStrips/plasmic-props";
import { BLOG_ENTRY_PLASMIC_PROPS } from "@stadium/shared/src/components/plasmic/BlogEntry/plasmic-props";
import { BENTO_GRID_PLASMIC_PROPS } from "@stadium/shared/src/components/plasmic/BentoGrid/plasmic-props";
import { STATIC_CATALOG_ENTRIES_PLASMIC_PROPS } from "@stadium/shared/src/components/plasmic/StaticCatalogEntries/plasmic-props";
import { O03_PLASMIC_PROPS } from "@stadium/shared/src/components/plasmic/O03/plasmic-props";
import { A01_PLASMIC_PROPS } from "@stadium/shared/src/components/plasmic/A01/plasmic-props";
import { TESTIMONIAL_PLASMIC_PROPS } from "@stadium/shared/src/components/plasmic/Testimonial/plasmic-props";

import {
  BANNER,
  CATALOG_ENTRIES,
  STATIC_CATALOG_ENTRIES,
  PRODUCT_LISTING_CAROUSEL,
  SHOP_MENU_WRAPPER,
  SWAGMAGIC_GLOBAL_CONTEXT_PROVIDER,
  HERO,
  TESTIMONIAL,
} from "@constants/plasmic/component-name";

import CatalogEntriesClient from "@components/swagmagic/dynamic/slices/catalog-entries/client";
import ProductListingCarouselClient from "@components/swagmagic/dynamic/slices/product-listing-carousel/client";
import ShopMenuWrapper from "@components/swagmagic/dynamic/slices/shop-menu-wrapper";
import SwagmagicGlobalContextProvider from "@contexts/plasmic/global-context";
import Banner from "@stadium/shared/src/components/plasmic/Banner";
import Hero from "@stadium/shared/src/components/plasmic/Hero";
import LogoStrips from "@stadium/shared/src/components/plasmic/LogoStrips";
import BlogEntry from "@stadium/shared/src/components/plasmic/BlogEntry";
import BentoGrid from "@stadium/shared/src/components/plasmic/BentoGrid";
import StaticCatalogEntries from "@stadium/shared/src/components/plasmic/StaticCatalogEntries";
import O03 from "@stadium/shared/src/components/plasmic/O03";
import A01 from "@stadium/shared/src/components/plasmic/A01";
import Testimonial from "@stadium/shared/src/components/plasmic/Testimonial";

PLASMIC.registerGlobalContext(SwagmagicGlobalContextProvider, {
  name: SWAGMAGIC_GLOBAL_CONTEXT_PROVIDER,
  importPath: "@contexts/plasmic/global-context",
  providesData: true,
  props: {},
});

PLASMIC.registerComponent(CatalogEntriesClient, {
  name: CATALOG_ENTRIES,
  importPath: "@components/swagmagic/dynamic/slices/catalog-entries/client",
  props: CatalogEntriesPlasmicProps,
});

PLASMIC.registerComponent(ShopMenuWrapper, {
  name: SHOP_MENU_WRAPPER,
  importPath: "@components/swagmagic/dynamic/slices/shop-menu-wrapper",
  props: ShopMenuWrapperPlasmicProps,
});

PLASMIC.registerComponent(ProductListingCarouselClient, {
  name: PRODUCT_LISTING_CAROUSEL,
  importPath: "@components/swagmagic/dynamic/slices/product-listing-carousel/client",
  props: ProductListingCarouselPlasmicProps,
});

PLASMIC.registerComponent(Banner, {
  name: BANNER,
  displayName: BANNER,
  importPath: "@stadium/shared/src/components/plasmic/Banner",
  props: BANNER_PLASMIC_PROPS,
});

PLASMIC.registerComponent(Hero, {
  name: HERO,
  displayName: HERO,
  importPath: "@stadium/shared/src/components/plasmic/Hero",
  props: HERO_PLASMIC_PROPS,
});

PLASMIC.registerComponent(LogoStrips, {
  name: "LogoStrips",
  displayName: "Logo Strips",
  importPath: "@stadium/shared/src/components/plasmic/LogoStrips",
  props: LOGO_STRIPS_PLASMIC_PROPS,
});

PLASMIC.registerComponent(BentoGrid, {
  name: "BentoGrid",
  displayName: "Bento Grid",
  importPath: "@stadium/shared/src/components/plasmic/BentoGrid",
  props: BENTO_GRID_PLASMIC_PROPS,
});

PLASMIC.registerComponent(StaticCatalogEntries, {
  name: STATIC_CATALOG_ENTRIES,
  displayName: "Static Catalog Entries",
  importPath: "@stadium/shared/src/components/plasmic/StaticCatalogEntries",
  props: STATIC_CATALOG_ENTRIES_PLASMIC_PROPS,
});

PLASMIC.registerComponent(O03, {
  name: "O03",
  displayName: "O-03",
  importPath: "@stadium/shared/src/components/plasmic/O03",
  props: O03_PLASMIC_PROPS,
});

PLASMIC.registerComponent(BlogEntry, {
  name: "BlogEntry",
  displayName: "Blog Entry",
  importPath: "@stadium/shared/src/components/plasmic/BlogEntry",
  props: BLOG_ENTRY_PLASMIC_PROPS,
});

PLASMIC.registerComponent(A01, {
  name: "A01",
  displayName: "A01",
  importPath: "@stadium/shared/src/components/plasmic/A01",
  props: A01_PLASMIC_PROPS,
});

PLASMIC.registerComponent(Testimonial, {
  name: TESTIMONIAL,
  displayName: TESTIMONIAL,
  importPath: "@stadium/shared/src/components/plasmic/Testimonial",
  props: TESTIMONIAL_PLASMIC_PROPS,
});
